import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Portal from '../Portal';
import { API_URL } from '../../config/apiConfig';
import { useTracking } from '../../hooks/useTracking';

interface GenerationResultModalProps {
  open: boolean;
  videoUrl: string;
  onClose: () => void;
  sourcePage?: 'lander-1' | 'image-to-video'; // Add source page prop to determine button behavior
}

export default function GenerationResultModal({
  open,
  videoUrl,
  onClose,
  sourcePage = 'lander-1', // Default to lander-1 for backward compatibility
}: GenerationResultModalProps) {
  const [shareToast, setShareToast] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const navigationIntentRef = useRef(false); // Guard against double navigation
  const navigate = useNavigate();
  const { track } = useTracking();

  // Lock scroll when overlay is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      
      // Prevent body scroll on mobile while modal open
      const preventScroll = (e: TouchEvent) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
        }
      };
      
      document.body.addEventListener('touchmove', preventScroll, { passive: false });
      
      return () => {
        document.body.style.overflow = '';
        document.body.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [open]);

  if (!open) return null;

  const handleDownload = async () => {
    const filename = `generated-video-${Date.now()}.mp4`;
    
    // Try backend proxy first to avoid CORS issues
    const downloadUrl = `${API_URL}/api/assets/download?video_url=${encodeURIComponent(videoUrl)}&filename=${encodeURIComponent(filename)}`;
    
    try {
      // Fetch the video as a blob
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      return; // Success
    } catch (backendError) {
      console.warn('Backend download failed, trying direct S3 fetch:', backendError);
      
      // Fallback: Try fetching directly from S3
      try {
        const response = await fetch(videoUrl, {
          method: 'GET',
          mode: 'cors',
        });
        
        if (!response.ok) {
          throw new Error(`S3 fetch failed: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create download link
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Clean up blob URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        return; // Success
      } catch (s3Error) {
        console.error('S3 direct fetch also failed:', s3Error);
        // If both fail, try using XMLHttpRequest as last resort
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', videoUrl, true);
          xhr.responseType = 'blob';
          
          await new Promise((resolve, reject) => {
            xhr.onload = () => {
              if (xhr.status === 200) {
                const blob = xhr.response;
                const blobUrl = window.URL.createObjectURL(blob);
                
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                resolve(true);
              } else {
                reject(new Error(`XHR failed: ${xhr.status}`));
              }
            };
            xhr.onerror = () => reject(new Error('XHR network error'));
            xhr.send();
          });
        } catch (xhrError) {
          console.error('All download methods failed:', xhrError);
          // Last resort: show error message
          alert('Download failed. Please right-click on the video and select "Save video as..." to download.');
        }
      }
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.origin + '/video/' + encodeURIComponent(videoUrl);
    
    // Try Web Share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this AI-generated video',
          text: 'I created this video with GenAnything',
          url: shareUrl,
        });
        return;
      } catch (error) {
        // User cancelled or share failed, fall back to clipboard
        if ((error as Error).name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    }
    
    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy link. Please try again.');
    }
  };

  const handleGenerateAnother = () => {
    // Track button click immediately (before any guards) for accurate analytics
    track('lander1_remove_watermark_clicked', {
      source_page: sourcePage,
      video_url: videoUrl,
      has_url_hash: !!window.location.hash,
      timestamp: Date.now(),
    });
    
    // Prevent double-navigation (removed overly defensive userHasInteracted check)
    if (navigationIntentRef.current) {
      console.log('[GenerationResultModal] Navigation already in progress, ignoring duplicate click');
      return;
    }
    
    // Prevent double-click navigation
    if (isNavigating) {
      console.log('[GenerationResultModal] Navigation already in progress (state check), ignoring duplicate click');
      return;
    }
    
    navigationIntentRef.current = true;
    setIsNavigating(true);
    
    if (sourcePage === 'image-to-video') {
      // For image-to-video page, refresh to reset the page state
      window.location.reload();
    } else {
      // For lander-1 page, navigate to upgrade page
      // OPTION C: Clean URL hash before navigation to prevent OAuth re-triggering
      if (window.location.hash) {
        console.log('[GenerationResultModal] 🧹 Cleaning URL hash before navigation to prevent OAuth issues');
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      
      // FIX 1: Clear lander-1 modal state before navigating to /upgrade
      // CRITICAL: This prevents the processing modal from appearing on upgrade page
      localStorage.removeItem('genanything_lander1_modal_state');
      console.log('[GenerationResultModal] 🧹 Cleared lander-1 modal state before navigating to /upgrade');
      
      // REVENUE PROTECTION: Use window.location.href instead of navigate()
      // This completely breaks the history chain and prevents back button from returning to /lander-1
      // Critical for mobile users from Meta ads who might accidentally hit back button
      console.log('[GenerationResultModal] Navigating to /upgrade (breaking history chain)');
      window.location.href = '/upgrade';
    }
  };

  return (
    <Portal>
      {/* Share Toast */}
      {shareToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10000] animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="bg-green-500/90 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <span>✅</span>
            <span className="font-medium">Link copied</span>
          </div>
        </div>
      )}

      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-3 md:p-4"
        onClick={(e) => {
          e.stopPropagation();
          // Prevent any backdrop clicks from propagating
        }}
        onTouchStart={(e) => {
          // iOS: Prevent touch from propagating through backdrop
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          // Android: Prevent delayed click events
          e.stopPropagation();
        }}
      >
        <div className="w-full max-w-[90vw] h-[90vh] max-h-[90vh] flex flex-col">
          {/* Main Content Card */}
          <div className="bg-bg-panel border border-border rounded-modal shadow-lg flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex justify-center items-center p-3 md:p-4 lg:p-6 flex-shrink-0 border-b border-border">
              <h2 className="text-h1 md:text-h1 lg:text-display font-medium text-white">
                🎉 Your Video is Complete!
              </h2>
            </div>

            {/* Video Player - Takes up available space */}
            <div className="flex-1 min-h-0 flex items-center justify-center p-3 md:p-4 lg:p-6">
              <div className="w-full h-full bg-black/20 rounded-lg overflow-hidden flex items-center justify-center">
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full object-contain rounded-lg"
                  onError={(e) => {
                    console.error('❌ [GenerationResultModal] Video load error:', e);
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="p-3 md:p-4 lg:p-6 space-y-3 md:space-y-4 flex-shrink-0 border-t border-border">
              {/* Generate Another Video Button - Full width */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setUserHasInteracted(true);
                  handleGenerateAnother();
                }}
                onTouchStart={(e) => {
                  // Mark interaction on touch start (iOS Safari needs this)
                  e.stopPropagation();
                  setUserHasInteracted(true);
                }}
                onTouchEnd={(e) => {
                  // Prevent ghost clicks that fire 300ms after touch on some Android browsers
                  e.stopPropagation();
                  e.preventDefault();
                  // CRITICAL: Call handler here since preventDefault blocks onClick on mobile
                  handleGenerateAnother();
                }}
                disabled={isNavigating}
                className="w-full bg-[#F9F7EA] text-[#0f1218] px-4 md:px-6 py-3 min-h-[44px] rounded-button font-medium hover:bg-[#E7D7B1] transition-colors flex items-center justify-center gap-2 text-body disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
                {sourcePage === 'lander-1' ? 'Remove Watermark' : 'Generate Another Video'}
              </button>
              
              {/* Download and Share (half width each) */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <button
                  onClick={handleDownload}
                  className="bg-[#E7D7B1] text-black px-4 md:px-6 py-3 min-h-[44px] rounded-button font-medium hover:bg-[#DCC894] transition-colors flex items-center justify-center gap-2 text-body"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  <span className="hidden sm:inline">Download Video</span>
                  <span className="sm:hidden">Download</span>
                </button>
                <button
                  onClick={handleShare}
                  className="bg-hover-strong text-white px-4 md:px-6 py-3 min-h-[44px] rounded-button font-medium hover:bg-hover-strong/80 transition-colors border border-border flex items-center justify-center gap-2 text-body"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
